import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import config from '../config';

interface InvoiceUploadProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  /** Called with any newly-created invoices that still need a "is this a new expense, or proof
   * of an existing transaction?" decision (Invoice.confirmed === false) — i.e. every plain
   * upload that wasn't already attached to a transaction at upload time. */
  onNewPendingInvoices?: (invoices: any[]) => void;
}

interface UploadResult {
  filename: string;
  success: boolean;
  message?: string;
  isDuplicate?: boolean;
  invoice?: any;
}

const InvoiceUpload: React.FC<InvoiceUploadProps> = ({ open, onClose, onUploadSuccess, onNewPendingInvoices }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
    setMessage(null);
    setUploadResults([]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one PDF file.' });
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setMessage({ type: 'error', text: 'Authentication required. Please log in again.' });
      return;
    }

    setUploading(true);
    setMessage(null);
    setUploadProgress(0);
    const results: UploadResult[] = [];

    try {
      // Upload files one by one
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await fetch(`${config.apiUrl}/api/invoices/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          const data = await response.json();

          if (response.ok) {
            results.push({
              filename: file.name,
              success: true,
              message: 'Uploaded successfully',
              invoice: data.invoice,
            });
          } else if (response.status === 409) {
            // data.invoice is the existing match this file's content hash collided with —
            // name it explicitly rather than a generic "already exists", so re-uploading
            // something you're pretty sure you deleted doesn't read as an unexplained dead end.
            const existing = data.invoice;
            const existingName = existing?.contractor_name || 'an existing invoice';
            const existingDate = existing?.invoice_date ? ` (${existing.invoice_date})` : '';
            results.push({
              filename: file.name,
              success: false,
              isDuplicate: true,
              message: `⚠️ Skipped — already uploaded as ${existingName}${existingDate}`
            });
          } else {
            results.push({
              filename: file.name,
              success: false,
              message: data.error || 'Upload failed'
            });
          }
        } catch (error) {
          results.push({
            filename: file.name,
            success: false,
            message: 'Network error'
          });
        }

        // Update progress
        setUploadProgress(((i + 1) / files.length) * 100);
      }

      setUploadResults(results);

      const pendingInvoices = results.filter(r => r.success && r.invoice && r.invoice.confirmed === false).map(r => r.invoice);
      if (pendingInvoices.length > 0) onNewPendingInvoices?.(pendingInvoices);

      const successCount = results.filter(r => r.success).length;
      const skippedCount = results.filter(r => !r.success && r.isDuplicate).length;
      const failCount = results.filter(r => !r.success && !r.isDuplicate).length;

      if (successCount > 0 || skippedCount > 0) {
        const parts = [];
        if (successCount > 0) parts.push(`${successCount} uploaded`);
        if (skippedCount > 0) parts.push(`${skippedCount} skipped (duplicates)`);
        if (failCount > 0) parts.push(`${failCount} failed`);
        
        setMessage({
          type: successCount > 0 ? 'success' : 'warning',
          text: parts.join(', ')
        });

        if (failCount === 0) {
          // Everything resolved cleanly (uploaded or a harmless duplicate skip) — close and
          // return to Accounting instead of leaving the dialog sitting there inviting a re-click.
          // Still wait 2s first for preview generation to complete before the parent refetches.
          setTimeout(() => {
            onUploadSuccess();
            handleClose();
          }, 2000);
        } else {
          // A genuine failure happened — keep the dialog open so the results (which files
          // failed) stay visible instead of vanishing before the user can read them.
          setTimeout(() => {
            onUploadSuccess();
          }, 2000);
        }
      } else {
        setMessage({
          type: 'error',
          text: 'All uploads failed. Please check the files and try again.'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Failed to upload invoices' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setMessage(null);
      setUploadResults([]);
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Invoices</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {/* Dropzone */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop invoices here' : 'Drag & drop invoice PDFs here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to select files
            </Typography>
          </Box>

          {/* File list */}
          {files.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({files.length}):
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      !uploading && (
                        <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Upload progress */}
          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading... {Math.round(uploadProgress)}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Upload results */}
          {uploadResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Results:
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {uploadResults.map((result, index) => {
                  const isDuplicate = result.isDuplicate;
                  const color = result.success ? 'success.main' : (isDuplicate ? 'warning.main' : 'error.main');
                  
                  return (
                    <ListItem key={index}>
                      <ListItemText
                        primary={result.filename}
                        secondary={result.message}
                        primaryTypographyProps={{
                          color: color
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {uploadResults.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={files.length === 0 || uploading}
        >
          {uploading ? 'Uploading...' : `Upload ${files.length > 0 ? `(${files.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceUpload;
